import { offerPasswordSaving } from '../platform/passwordSaving';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { login, logout } from '../store/authSlice';
import { clearSession, saveToken } from '../utils/authUtils';
import { Container, TextField, Button, Box, Typography, Alert, IconButton, InputAdornment, Link } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../utils/axiosConfig';
import { runtime } from '../platform/runtime';
import { biometrics } from '../platform/biometrics';
import { API_FAILURE } from '../platform/apiPolicy.mjs';



import BiometricLogin from './BiometricLogin';

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const submissionPending = useRef(false);
    const formRef = useRef(null);
    const isIOS = runtime.platform() === 'ios';
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submissionPending.current) return;
        submissionPending.current = true;
        setSubmitting(true);
        // Keep the browser's inputs authoritative: Password AutoFill may replace
        // their values without notifying React. Read both together on submit.
        const form = new FormData(e.currentTarget);
        const username = form.get('username');
        const password = form.get('password');
        try {

            const body = { username, password, ...(runtime.isNative() ? { nativeSession: true } : {}) };
            // For login, we don't want to send the auth token since we don't have one yet
            const response = await api.post(`/api/caregiverLogin`, body, {
                headers: {
                    "Authorization": '' // Override the default Authorization header
                }
            });
            setError('');
            const data = response.data;
            if (data.isFirstLogin) {
                navigate('/createAccount');
                return;
            }
            if (data.token && data.units) {
                const { token, exp, units, isFirstLogin, signedEula } = data;
                if (runtime.isNative()) await saveToken(token, exp, data);
                else await saveToken(token, exp);
                if (runtime.isNative()) biometrics.didLogin();
                await offerPasswordSaving(username, password);
                dispatch(login({
                    token,
                    units,
                    signedEula,
                    isFirstLogin,
                    user: data.user,
                }));
                if (!signedEula) {
                    navigate('/eula');
                    return;
                } else {
                    navigate('/dashboard'); // Redirect to dashboard if not first login
                }
                if (runtime.isWeb()) setTimeout(() => { // web auto logout after 24 hours
                    dispatch(logout());
                    void clearSession();
                    navigate('/login');
                }, 24 * 60 * 60 * 1000); // 24 hours in milliseconds
            } else {
                setError('Invalid login response. Please try again.');
            }


        } catch (error) {
            if (error.response && error.response.status === 401) {
                setError('Incorrect username or password.');
            } else if (error.response && error.response.status >= 500) {
                setError('Server error. Please try again later.');
            } else if (error.connectFailureType === API_FAILURE.TIMEOUT || ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)) {
                setError('The connection timed out. Check your connection and try logging in again.');
            } else if (error.connectFailureType === API_FAILURE.NETWORK || error.code === 'ERR_NETWORK') {
                setError('Unable to connect to Connect App 365. Check your Wi-Fi or cellular connection and try logging in again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            submissionPending.current = false;
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="xs">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="90vh"
            >
                <Typography variant="h4" component="h1" gutterBottom>
                    Login
                </Typography>
                <BiometricLogin />
                {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>} {/* Display error message */}
                <Box component="form" ref={formRef} onSubmit={handleSubmit} sx={{ mt: 1 }}
                    onFocusCapture={(event) => {
                        if (isIOS && event.target.tagName === 'INPUT') setShowPassword(false);
                    }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Username"
                        name="username"
                        autoComplete="username"
                        inputProps={{
                            inputMode: 'email',
                            autoCapitalize: 'none',
                            autoCorrect: 'off',
                            spellCheck: false
                        }}
                        autoFocus={runtime.isWeb()}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        type="button"
                                        sx={{ minWidth: 48, minHeight: 48 }}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        onClick={() => {
                                            // iOS can stop replacing AutoFill credentials while
                                            // the password is a text field. Reveal for inspection;
                                            // restore password semantics before editing/AutoFill.
                                            if (isIOS && !showPassword && formRef.current?.contains(document.activeElement)) {
                                                document.activeElement.blur();
                                            }
                                            setShowPassword((visible) => !visible);
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        type="submit"
                        disabled={submitting}
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3, mb: 2, minHeight: 48 }}
                    >
                        Login
                    </Button>

                </Box>
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', mt: 1, flexWrap: 'wrap', gap: 1 }}
                >
                    <Link component={RouterLink} to="/forgotPassword"
                        variant="body2"
                        align="center"
                        sx={{ minHeight: 48, display: 'inline-flex', alignItems: 'center', color: 'text.primary' }}
                    >
                        Forgot Password
                    </Link>
                    <Link component={RouterLink} to="/createAccount"
                        variant="body2"
                        align="center"
                        sx={{ minHeight: 48, display: 'inline-flex', alignItems: 'center', color: 'text.primary' }}
                    >
                        Create Account
                    </Link>
                </Box>
            </Box>
        </Container>
    );
};

export default Login;
